<script setup lang="ts">
    import { usePlannerSidebarViewModel } from './usePlannerSidebarViewModel';
    import { PLANNER_SIDEBAR_TARGETS } from '@/domain/analytics/analyticsTargets';

    type SidebarPanelItem = {
        icon: string;
        iconHover: string;
        label: string;
        key: 'stem' | 'cup';
        width: string;
        height: string;
    };

    const items: SidebarPanelItem[] = [
        {
            iconHover: 'f:stem-light',
            icon: 'f:stem',
            label: 'Stem',
            key: 'stem',
            width: '27px',
            height: '32px',
        },
        {
            iconHover: 'f:cup-light',
            icon: 'f:cup',
            label: 'Cup',
            key: 'cup',
            width: '28px',
            height: '30px',
        },
    ];

    const vm = usePlannerSidebarViewModel();
</script>

<template>
    <v-navigation-drawer
        absolute
        color="grey-transparent"
        :rail="true"
        permanent
        floating
        class="planner-sidebar"
        :class="vm.showContent ? 'drawer-expanded' : ''"
        data-testid="planner-sidebar-closed"
    >
        <v-list-item
            v-for="(item, index) in items"
            :key="index"
            class="mb-1 px-1 pl-1 planner-sidebar-item"
            :disabled="vm.disableSidebar"
            :ripple="false"
            @click="vm.openPanel(item.key)"
        >
            <template #prepend>
                <v-hover>
                    <template #default="{ isHovering, props }">
                        <v-btn
                            v-bind="props"
                            :data-analytics-id="PLANNER_SIDEBAR_TARGETS[item.key]"
                            elevation="0"
                            icon
                            :color="
                                vm.expandedPanel === item.key
                                    ? 'white'
                                    : isHovering
                                      ? 'dark-blue'
                                      : 'planner-surface'
                            "
                            class="pa-0"
                            height="44"
                            width="44"
                        >
                            <v-icon
                                :icon="
                                    vm.expandedPanel === item.key
                                        ? item.icon
                                        : isHovering
                                          ? item.iconHover
                                          : item.icon
                                "
                                :width="item.width"
                                :height="item.height"
                            />
                        </v-btn>
                    </template>
                </v-hover>
            </template>

            <v-list-item-title>{{ item.label }}</v-list-item-title>
        </v-list-item>

        <template #append>
            <v-list-item
                v-if="vm.developerToolsEnabled"
                class="mb-0 px-1 planner-sidebar-item"
                :disabled="vm.disableSidebar"
                :ripple="false"
                @click="vm.openDevTools()"
            >
                <v-btn
                    elevation="0"
                    icon
                    :color="vm.devToolsSelected ? 'white' : 'grey-light-1'"
                    class="mr-0"
                    height="44"
                    width="44"
                >
                    <v-icon icon="mdi-cog" size="24px"></v-icon>
                </v-btn>
            </v-list-item>
        </template>
    </v-navigation-drawer>
</template>

<style scoped lang="scss">
    .v-navigation-drawer.bg-grey-transparent {
        background-color: rgba(248, 248, 248, 0.87);
    }

    .v-navigation-drawer:not(.drawer-expanded) {
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
    }

    .planner-sidebar:deep(.v-navigation-drawer__content) {
        margin-top: 24px;
    }

    .planner-sidebar-item:hover {
        :deep(.v-list-item__overlay) {
            background-color: rgb(var(--v-theme-grey-light-1));
        }
    }

    // HACK — workaround for Vuetify rail-mode grid; should be replaced if
    // this is revisited (e.g. if the drawer ever becomes expandable, gate
    // this on `.v-navigation-drawer--rail`).
    //
    // Centre the icon column within the 56px rail. Vuetify's default grid
    // is `max-content 1fr auto`; the 1fr content column has an `auto` min
    // size that, even with Vuetify's rail-mode `__content { min-width: 0 }`,
    // still absorbs whatever leftover width remains after the prepend track —
    // and the title (now in the default slot, rendered inside __content)
    // leaks its first letter through __content's `overflow: hidden`.
    // Replacing 1fr with a fixed 0 collapses the content track outright;
    // justify-content: center distributes the leftover padding-room evenly
    // either side of the icon.
    .planner-sidebar-item {
        grid-template-columns: max-content 0 auto;
        justify-content: center;
    }
</style>
